import { useCallback, useEffect, useRef, useState } from 'react'
import * as api from '../api/client'

/**
 * Recording a customer speaking, and turning it into text they can read before they send it.
 *
 * WHY NOT THE BROWSER'S OWN SPEECH RECOGNITION
 *   window.SpeechRecognition is free and needs no server. It was not used, for two reasons that matter to
 *   this audience specifically. It must be TOLD the language before it will listen, so a customer would
 *   have to find a language menu before speaking, and someone who mixes English and Hindi in one sentence
 *   - which is ordinary here - has no correct option to pick. And it is absent from Firefox and unreliable
 *   in several Android browsers.
 *
 *   Sending the audio to Cashit costs one request, and in return the language is DETECTED, identically for
 *   every customer on every browser.
 *
 * WHAT THE CUSTOMER CONTROLS
 *   The transcript lands in the composer as editable text. Nothing is sent on their behalf: speech
 *   recognition gets names wrong, and "Redmi Note 12" heard as "Retme note twelve" would search for a
 *   device that does not exist. They read it, fix it if needed, and press send.
 */

/** The longest single recording. Past this the file grows for no benefit - nobody describes a phone for a minute. */
const MAX_SECONDS = 60

/**
 * Whether the PAGE ITSELF is allowed a microphone, before any customer or browser setting is consulted.
 *
 * A Permissions-Policy header with microphone=() disables the feature for the whole document, and no
 * permission a customer grants can override it: getUserMedia throws NotAllowedError and
 * navigator.permissions reports "denied" - indistinguishable, from inside the page, from the customer
 * having blocked the site themselves. sellgpt.in shipped exactly that header, so every customer who
 * allowed the microphone by hand was still refused and told to change a setting that could not help.
 *
 * Checking it here means a server sending that header hides the button instead, which is the honest
 * outcome: the site cannot record, and that is nothing the customer can fix.
 */
function documentAllowsMicrophone(): boolean {
  const policy = (document as Document & { featurePolicy?: { allowsFeature(f: string): boolean } }).featurePolicy
  try {
    return policy ? policy.allowsFeature('microphone') : true
  } catch {
    return true // not supported by this browser; fall through to asking, as before
  }
}

/**
 * Why the microphone did not open, in words a customer can act on.
 *
 * The browser's own DOMException names are the only reliable signal here, and they mean genuinely
 * different things: a blocked permission needs a setting changed, a missing device needs a microphone,
 * and a busy device needs another application closed. One message covering all three tells nobody what to
 * do. The permission state is consulted only for the first of those, and only to distinguish "you just
 * said no" from "this site was blocked earlier and the browser will not even ask".
 */
async function explain(caught: unknown): Promise<string> {
  const name = caught instanceof DOMException ? caught.name : ''

  if (name === 'NotFoundError' || name === 'DevicesNotFoundError')
    return 'No microphone was found on this device. You can still type.'

  if (name === 'NotReadableError' || name === 'TrackStartError' || name === 'AbortError')
    return 'Your microphone is being used by another app. Close it and tap the microphone again.'

  let blockedEarlier = false
  try {
    const status = await navigator.permissions?.query({ name: 'microphone' as PermissionName })
    blockedEarlier = status?.state === 'denied'
  } catch {
    /* not queryable here - the general wording below still applies */
  }

  return blockedEarlier
    ? 'Your browser has blocked the microphone for this site. Open the lock icon next to the address, set Microphone to Allow, then reload the page.'
    : 'The microphone was not allowed. Tap it again and choose Allow.'
}

export type VoiceState = 'unsupported' | 'idle' | 'asking' | 'recording' | 'transcribing' | 'denied'

export interface VoiceInput {
  state: VoiceState
  /** Seconds recorded so far, for the button to count up. */
  seconds: number
  /** Null until something goes wrong, and cleared the moment recording starts again. */
  error: string | null
  start: () => void
  stop: () => void
  cancel: () => void
}

/**
 * @param onTranscript Called with the recognised text and the detected language. The caller decides what
 *                     to do with it - SellGPT puts it in the composer rather than sending it.
 * @param enabled      False when the server says voice input is not configured, which hides the button.
 */
export function useVoiceInput(
  onTranscript: (text: string, language?: string | null) => void,
  enabled: boolean,
  /** The language the customer named, or undefined to let the model detect it. */
  language?: string,
): VoiceInput {
  const [state, setState] = useState<VoiceState>('idle')
  const [seconds, setSeconds] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const recorder = useRef<MediaRecorder | null>(null)
  const chunks = useRef<Blob[]>([])
  const ticker = useRef<number | null>(null)
  const discarded = useRef(false)

  // Whether this browser can record at all. Checked once, so a browser without MediaRecorder or a page
  // served over plain http (where getUserMedia does not exist) shows no microphone rather than a button
  // that fails when pressed.
  const supported =
    typeof window !== 'undefined' &&
    typeof window.MediaRecorder !== 'undefined' &&
    Boolean(navigator.mediaDevices?.getUserMedia) &&
    documentAllowsMicrophone()

  const stopTicker = useCallback(() => {
    if (ticker.current !== null) {
      window.clearInterval(ticker.current)
      ticker.current = null
    }
  }, [])

  /** Releases the microphone. Without this the browser's recording indicator stays on after the sale. */
  const release = useCallback(() => {
    recorder.current?.stream.getTracks().forEach((track) => track.stop())
    recorder.current = null
  }, [])

  useEffect(
    () => () => {
      stopTicker()
      release()
    },
    [stopTicker, release],
  )

  /**
   * Recover when the customer allows the microphone from the browser's own controls.
   *
   * Without this, someone who follows the instructions in the error - open the lock icon, switch the
   * microphone to Allow - comes back to a message still telling them it is blocked, because nothing in the
   * page ever heard about the change.
   */
  useEffect(() => {
    if (!supported) return
    let status: PermissionStatus | null = null
    const onChange = () => {
      if (status?.state === 'granted' || status?.state === 'prompt') {
        setState((current) => (current === 'denied' ? 'idle' : current))
        setError(null)
      }
    }

    void (async () => {
      try {
        status = (await navigator.permissions?.query({ name: 'microphone' as PermissionName })) ?? null
        status?.addEventListener('change', onChange)
      } catch {
        /* not queryable here; the error message still tells them what to do */
      }
    })()

    return () => status?.removeEventListener('change', onChange)
  }, [supported])

  const start = useCallback(() => {
    if (!supported || !enabled) return
    setError(null)
    discarded.current = false

    void (async () => {
      /**
       * ALWAYS ASK THE BROWSER. Never decide in advance that it would refuse.
       *
       * An earlier version read navigator.permissions first and gave up when it said "denied". That was
       * wrong twice over: the query can disagree with reality - it is cached per document and lags behind
       * a setting the customer has just changed - and it is the getUserMedia call itself, not the query,
       * that makes a browser show its prompt. Refusing to call it meant a customer who had JUST granted
       * access was still told they were blocked, with nothing they could do about it.
       *
       * So the call is always made. The permission state is consulted afterwards, and only to explain a
       * failure accurately.
       */
      setState('asking')

      let stream: MediaStream
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      } catch (caught) {
        setState('denied')
        setError(await explain(caught))
        return
      }

      // Let the browser pick its own container. Chrome gives webm/opus, Safari mp4 - both are accepted by
      // the server, and naming a codec Safari does not have would throw here instead of recording.
      const mime = ['audio/webm', 'audio/mp4', 'audio/ogg'].find(
        (type) => MediaRecorder.isTypeSupported?.(type) ?? false,
      )

      const media = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined)
      recorder.current = media
      chunks.current = []

      media.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.current.push(event.data)
      }

      media.onstop = () => {
        stopTicker()
        const blob = new Blob(chunks.current, { type: media.mimeType || 'audio/webm' })
        release()

        if (discarded.current || blob.size < 1200) {
          // A recording this small is a tap, not a sentence. Transcribing it would spend a request to be
          // told there were no words in it.
          setState('idle')
          setSeconds(0)
          return
        }

        setState('transcribing')
        void api
          .transcribe(blob, language)
          .then((result) => {
            if (!result.available) {
              setState('unsupported')
              return
            }
            if (result.text.trim()) onTranscript(result.text.trim(), result.language)
            else setError("I couldn't catch that. Try again, or type it.")
            setState('idle')
          })
          .catch((caught) => {
            setError(caught instanceof api.ApiError ? caught.message : "I couldn't catch that. Try again, or type it.")
            setState('idle')
          })
          .finally(() => setSeconds(0))
      }

      media.start()
      setState('recording')
      setSeconds(0)
      ticker.current = window.setInterval(() => {
        setSeconds((value) => {
          const next = value + 1
          // Stopping itself at the cap is kinder than refusing the upload afterwards: the customer keeps
          // whatever they had said instead of losing the whole recording.
          if (next >= MAX_SECONDS) media.stop()
          return next
        })
      }, 1000)
    })()
  }, [supported, enabled, language, onTranscript, stopTicker, release])

  const stop = useCallback(() => {
    if (recorder.current?.state === 'recording') recorder.current.stop()
  }, [])

  const cancel = useCallback(() => {
    discarded.current = true
    if (recorder.current?.state === 'recording') recorder.current.stop()
    else {
      stopTicker()
      release()
      setState('idle')
      setSeconds(0)
    }
  }, [stopTicker, release])

  return {
    state: !supported || !enabled ? 'unsupported' : state,
    seconds,
    error,
    start,
    stop,
    cancel,
  }
}
